import { useLingui } from '@lingui/react/macro';
import { ChevronDown } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';

import { useIsMobile } from '@/shared/mobile';
import { EmptyState } from '@/shared/page';
import { cn } from '@/shared/ui/cn';
import { ScrollArea } from '@/shared/ui/scroll-area';

import { amortizationDate, amortizationMoney, indexerText, scheduleTotal, type AmortizationResult, type PaymentScheduleItem } from './amortization';

export interface AmortizationScheduleProps {
  readonly items: readonly PaymentScheduleItem[] | null | undefined;
  /** Indexador pós-fixado: o Pagamento ganha `+ 100% CDI` (OperationDetails.tsx:344-349). */
  readonly indexer?: Pick<AmortizationResult, 'indexer' | 'indexerValue'> | null | undefined;
  /**
   * Em edição manual do plano (o estado local `customizedPaymentPlan`, não o flag salvo da operação): mostra
   * a coluna IOF e esconde Tarifas (OperationDetails.tsx:69-87; ButtonsManualCalculation.tsx:77).
   */
  readonly customPaymentPlan?: boolean;
  /** Coluna Tarifas: `includePaymentFixedCosts` e `product.paymentFixedCosts > 0` (OperationDetails.tsx:65-66). */
  readonly fixedCosts?: boolean;
  /** Rótulo por tenant da coluna Tarifas (`GetLabelForField('addedFixedCosts')`). */
  readonly fixedCostsLabel?: string;
  /** Título e subtítulo próprios; num bloco, quem titula é o `BlockFrame`. */
  readonly title?: ReactNode;
  readonly description?: ReactNode;
  /** Nome da tabela para leitor de tela. */
  readonly label?: string;
  readonly presentation?: 'web' | 'mobile';
  readonly className?: string;
}

interface Row {
  readonly key: string;
  readonly number: string;
  readonly due: string;
  readonly balance: string;
  readonly amortization: string;
  readonly interest: string;
  readonly financeTax: string;
  readonly payment: string;
  readonly fixedCosts: string;
}

function useRows({ items, indexer }: Pick<AmortizationScheduleProps, 'items' | 'indexer'>) {
  const { i18n, t } = useLingui();
  const locale = i18n.locale;
  const suffix = indexerText(indexer);
  const money = (value: unknown) => amortizationMoney(value, locale);
  const rows = (items ?? []).map(
    (item, index): Row => ({
      key: item.id ?? String(index),
      number: String(item.paymentNo ?? index),
      due: amortizationDate(item.dueDate, locale) ?? t`N/D`,
      balance: money(item.principalAmountInCents),
      amortization: money(item.amortization),
      interest: money(item.interest),
      financeTax: money(item.financeTax),
      payment: item.payment !== 0 && suffix ? `${money(item.payment)} + ${suffix}` : money(item.payment),
      fixedCosts: money(item.addedFixedCosts),
    }),
  );
  return { rows, total: money(scheduleTotal(items)) };
}

function Heading({ title, description }: Pick<AmortizationScheduleProps, 'title' | 'description'>) {
  if (!title) return null;
  return (
    <div className="flex flex-col gap-0.5">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
    </div>
  );
}

/**
 * Plano de pagamento (OperationDetails.tsx): Parcela, Vencimento, Saldo Devedor, Amortização, Juros,
 * IOF (plano customizado), Pagamento e Tarifas, com o Total. Web em tabela; iPhone em lista de
 * parcelas que abre o detalhe de cada uma. Só apresenta: o cálculo é do módulo.
 */
export function AmortizationSchedule(props: AmortizationScheduleProps) {
  const isMobile = useIsMobile();
  const presentation = props.presentation ?? (isMobile ? 'mobile' : 'web');
  return presentation === 'mobile' ? <ScheduleMobile {...props} /> : <ScheduleWeb {...props} />;
}

function EmptySchedule() {
  const { t } = useLingui();
  return <EmptyState compact title={t`Plano de pagamento ainda não calculado`} description={t`O plano aparece depois da simulação.`} />;
}

function ScheduleWeb({ items, indexer, customPaymentPlan = false, fixedCosts = false, fixedCostsLabel, title, description, label, className }: AmortizationScheduleProps) {
  const { t } = useLingui();
  const { rows, total } = useRows({ items, indexer });
  const name = label ?? t`Parcelas do plano de pagamento`;
  const withTax = customPaymentPlan;
  const withFixedCosts = fixedCosts && !customPaymentPlan;
  const head = 'sticky top-0 z-10 bg-card px-3 py-2.5 font-medium whitespace-nowrap shadow-[inset_0_-1px_0_var(--color-structural)]';
  const num = 'px-3 text-right font-mono whitespace-nowrap tabular-nums';
  return (
    <div data-slot="amortization-schedule" className={cn('flex min-w-0 flex-col gap-3', className)}>
      <Heading title={title} description={description} />
      {rows.length === 0 ? (
        <EmptySchedule />
      ) : (
        <ScrollArea
          axes="both"
          className="rounded-card border border-border bg-card"
          viewport={{ role: 'region', 'aria-label': name, tabIndex: 0, className: 'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset' }}
        >
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">{name}</caption>
            <thead>
              <tr className="text-xs text-muted-foreground">
                <th data-col="number" scope="col" className={cn(head, 'text-left')}>{t`Parcela`}</th>
                <th data-col="due" scope="col" className={cn(head, 'text-left')}>{t`Vencimento`}</th>
                <th data-col="balance" scope="col" className={cn(head, 'text-right')}>{t`Saldo Devedor`}</th>
                <th data-col="amortization" scope="col" className={cn(head, 'text-right')}>{t`Amortização`}</th>
                <th data-col="interest" scope="col" className={cn(head, 'text-right')}>{t`Juros`}</th>
                {withTax ? <th data-col="financeTax" scope="col" className={cn(head, 'text-right')}>{t`IOF`}</th> : null}
                <th data-col="payment" scope="col" className={cn(head, 'text-right')}>{t`Pagamento`}</th>
                {withFixedCosts ? <th data-col="fixedCosts" scope="col" className={cn(head, 'text-right')}>{fixedCostsLabel ?? t`Tarifas`}</th> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key} className="h-row border-b border-border last:border-b-0 hover:bg-muted">
                  <th data-col="number" scope="row" className="px-3 text-left font-mono font-normal tabular-nums">
                    {row.number}
                  </th>
                  <td data-col="due" className="px-3 font-mono whitespace-nowrap tabular-nums">{row.due}</td>
                  <td data-col="balance" className={num}>{row.balance}</td>
                  <td data-col="amortization" className={num}>{row.amortization}</td>
                  <td data-col="interest" className={num}>{row.interest}</td>
                  {withTax ? <td data-col="financeTax" className={num}>{row.financeTax}</td> : null}
                  <td data-col="payment" className={cn(num, 'font-medium text-foreground')}>{row.payment}</td>
                  {withFixedCosts ? <td data-col="fixedCosts" className={num}>{row.fixedCosts}</td> : null}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="h-row border-t border-structural text-sm font-semibold">
                <th scope="row" colSpan={withTax ? 6 : 5} className="px-3 text-right">
                  {t`Total`}
                </th>
                <td data-col="payment" className={num}>{total}</td>
                {withFixedCosts ? <td data-col="fixedCosts" /> : null}
              </tr>
            </tfoot>
          </table>
        </ScrollArea>
      )}
    </div>
  );
}

function ScheduleMobile({ items, indexer, customPaymentPlan = false, fixedCosts = false, fixedCostsLabel, title, description, label, className }: AmortizationScheduleProps) {
  const { t } = useLingui();
  const { rows, total } = useRows({ items, indexer });
  const [open, setOpen] = useState<string | null>(null);
  const baseId = useId();
  const name = label ?? t`Parcelas do plano de pagamento`;
  const withFixedCosts = fixedCosts && !customPaymentPlan;
  return (
    <div data-slot="amortization-schedule" className={cn('flex min-w-0 flex-col gap-3', className)}>
      <Heading title={title} description={description} />
      {rows.length === 0 ? (
        <EmptySchedule />
      ) : (
        <div className="flex flex-col rounded-2xl border border-border bg-card px-4 py-2">
          <p className="flex items-center justify-between py-2 text-sm text-muted-foreground">
            <span>{rows.length === 1 ? t`1 parcela` : t`${rows.length} parcelas`}</span>
            <span className="font-mono font-semibold text-foreground tabular-nums">{t`Total ${total}`}</span>
          </p>
          <ul aria-label={name} className="flex flex-col">
            {rows.map((row) => {
              const expanded = open === row.key;
              const panel = `${baseId}-${row.key}`;
              const details: readonly (readonly [string, string])[] = [
                [t`Saldo Devedor`, row.balance],
                [t`Amortização`, row.amortization],
                [t`Juros`, row.interest],
                ...(customPaymentPlan ? [[t`IOF`, row.financeTax] as const] : []),
                ...(withFixedCosts ? [[fixedCostsLabel ?? t`Tarifas`, row.fixedCosts] as const] : []),
              ];
              return (
                <li key={row.key} className="border-t border-border first:border-t-0">
                  <button
                    type="button"
                    aria-expanded={expanded}
                    aria-controls={panel}
                    onClick={() => setOpen(expanded ? null : row.key)}
                    className="flex min-h-12 w-full items-center gap-3 py-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="w-8 shrink-0 font-mono text-sm font-medium tabular-nums">
                      <span className="sr-only">{t`Parcela`} </span>
                      {row.number}
                    </span>
                    <span className="font-mono text-sm text-muted-foreground tabular-nums">{row.due}</span>
                    <span className="ml-auto text-right font-mono text-sm font-semibold tabular-nums">{row.payment}</span>
                    <ChevronDown aria-hidden className={cn('size-4 shrink-0 text-muted-foreground transition-transform duration-(--lume-dur-enter)', expanded && 'rotate-180')} />
                  </button>
                  <div id={panel} className="uyzy-accordion" data-open={expanded} inert={!expanded}>
                    <div className="min-h-0 overflow-hidden">
                      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 pb-3">
                        {details.map(([term, value]) => (
                          <div key={term} className="flex flex-col gap-0.5">
                            <dt className="text-xs text-muted-foreground">{term}</dt>
                            <dd className="font-mono text-sm tabular-nums">{value}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
